import { DateTime } from 'luxon';
import { SessionService } from 'services/SessionService';
import {
  initialFacilityViewState,
  initialCopyParamsState,
  initialScheduleState,
  initialContextMenuState,
  initialCreateShiftState,
} from 'store/reducers/scheduleSlice/index.initialState';
import { getWebVersion } from 'utils/getWebVersion';
import {
  ScheduleFacilityViewState,
  ScheduleReducerStateType,
} from 'store/reducers/scheduleSlice/scheduleReducer.types';

export function generateViewKey(facility_id: string | number): `view_${string}` {
  return `view_${facility_id}`;
}

export function persistScheduleState(state: ScheduleReducerStateType): ScheduleReducerStateType {
  const { id: user_id } = SessionService.getUser()!;

  const cachedState = {
    ...state,
    copyParams: initialCopyParamsState,
    contextMenu: initialContextMenuState,
    createShiftPopup: initialCreateShiftState,
  };
  if (!user_id) return state;
  localStorage.setItem(
    `${user_id}_SCHEDULE_STATE_${getWebVersion()}`,
    JSON.stringify({
      state: cachedState,
      expiry: DateTime.now().plus({ day: 1 }).startOf('day').toString(),
    }),
  );
  return state;
}

export function getCachedScheduleState(isOutputRawJSON: true): [string, string] | undefined;
export function getCachedScheduleState(
  isOutputRawJSON?: false,
): ScheduleReducerStateType | undefined;
export function getCachedScheduleState(
  isOutputRawJSON = false,
): ScheduleReducerStateType | [string, string] | undefined {
  const user = SessionService.getUser();

  if (!user) return undefined;
  const { id: user_id } = user;

  let isCacheValid = true;

  const cachedScheduleStates = Object.keys(localStorage).filter((key) =>
    key.includes('SCHEDULE_STATE'),
  );

  cachedScheduleStates.forEach((key) => {
    const user_key_from_key = key.split('_')[0];
    if (`${user_key_from_key}` !== `${user_id}`) {
      localStorage.removeItem(key);
      isCacheValid = false;
    }
  });

  const stringifiedState = localStorage.getItem(`${user_id}_SCHEDULE_STATE_${getWebVersion()}`);

  if (stringifiedState && isOutputRawJSON)
    return [stringifiedState, `${user_id}_SCHEDULE_STATE_${getWebVersion()}`];
  if (!stringifiedState) return undefined;

  const { state, expiry } = JSON.parse(stringifiedState);

  const now = DateTime.now();
  isCacheValid = now < DateTime.fromISO(expiry);

  if (!isCacheValid) {
    localStorage.removeItem(`${user_id}_SCHEDULE_STATE_${getWebVersion()}`);
    return undefined;
  }
  return { ...state, copyParams: initialCopyParamsState };
}

export function getPrevFacilityViewState(state: ScheduleReducerStateType): {
  facilityViewState: ScheduleFacilityViewState;
  selectedFacilityId: number;
} {
  return {
    facilityViewState: state[generateViewKey(state.selectedFacilityId)] || initialFacilityViewState,
    selectedFacilityId: state.selectedFacilityId,
  };
}

export function getInitialScheduleState(
  cachedScheduleState: ScheduleReducerStateType | undefined = undefined,
): ScheduleReducerStateType {
  const obj = JSON.parse(
    JSON.stringify({
      ...initialScheduleState,
      ...cachedScheduleState,
    }),
  );
  obj.shiftSideModal.isSideModalOpen = false;
  return cachedScheduleState ? obj : (initialScheduleState as unknown as ScheduleReducerStateType);
}

/**
 * Function that merges prepared payload to previous facility view state and returns a new facility view state state.
 * @param {Object}  state       Schedule Slice State
 * @param {Object}  payload     Payload prepared by Action calling this Function
 *
 * @return {Object} Passes mutated State to persistScheduleState which stores the new State to LocalStorage and returns it
 */

export function mergePayloadWithFacilityViewState(
  state: ScheduleReducerStateType,
  { payload }: { payload: Record<string, unknown> },
  persist = true,
): ScheduleReducerStateType {
  const { selectedFacilityId, facilityViewState } = getPrevFacilityViewState(state);
  const nextFacilityViewState = Object.entries(payload).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: value,
    };
  }, {});
  return persist
    ? persistScheduleState({
        ...state,
        [generateViewKey(selectedFacilityId)]: {
          ...facilityViewState,
          ...nextFacilityViewState,
        },
      } as ScheduleReducerStateType)
    : ({
        ...state,
        [generateViewKey(selectedFacilityId)]: {
          ...facilityViewState,
          ...nextFacilityViewState,
        },
      } as ScheduleReducerStateType);
}

export function mergePayloadWithState(
  state: ScheduleReducerStateType,
  { payload }: { payload: Record<string, unknown> },
  persist = true,
): ScheduleReducerStateType {
  return persist
    ? persistScheduleState({ ...state, ...payload } as ScheduleReducerStateType)
    : ({ ...state, ...payload } as ScheduleReducerStateType);
}
